import {
	TUpdateMePayload,
	TUpdatePasswordPayload,
	TTutorialKey,
	TTutorialStatus,
	TUser,
	TUserSettings,
} from "@/types";

import API from "./api";

class UserClass {
	/** Passkeys only: needs one, and the current password. 409 AUTH.LAST_SIGN_IN_METHOD without a passkey. */
	removePassword = (currentPassword: string): Promise<void> => {
		return API.delete("me/password", { data: { currentPassword } }).then(
			() => undefined
		);
	};

	/** Mails the confirmation link again. 429 past 3 in an hour. */
	resendEmailVerification = (): Promise<void> => {
		return API.post("me/email/verification").then(() => undefined);
	};

	fetchMe = (token?: string): Promise<TUser> => {
		return API.get(
			"me",
			token
				? {
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				: {}
		).then((res) => res.data);
	};

	updateAvatar = (file: File) => {
		const formData = new FormData();
		formData.append("avatar", file);

		return API.post("me/avatar", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		}).then((res) => res.data);
	};

	/** Answers the updated user, same shape as `GET /me`. */
	updateMe = (payload: TUpdateMePayload): Promise<TUser> => {
		return API.patch("me", payload).then((res) => res.data);
	};

	/** My rhythm: the working week and how long a billed day is. */
	fetchSettings = (): Promise<TUserSettings> => {
		return API.get("me/settings").then((res) => res.data);
	};

	/**
	 * Partial: an omitted field is left alone, `hoursPerDay: ""` clears it.
	 * `tutorials` records how a tour ended, by key; null shows it again.
	 */
	updateSettings = (
		payload: Partial<Pick<TUserSettings, "workDays" | "hoursPerDay">> & {
			tutorials?: Partial<Record<TTutorialKey, TTutorialStatus | null>>;
		}
	): Promise<TUserSettings> => {
		return API.put("me/settings", payload).then((res) => res.data);
	};

	/** 204; 400 on a wrong current password or an SSO-only account, 422 on validation. */
	updatePassword = (payload: TUpdatePasswordPayload): Promise<void> => {
		return API.patch("me/password", payload).then(() => {});
	};

	/**
	 * Deletes the account and everything it owns. 204; 400 on a wrong
	 * password, 503 when Stripe could not be reached (nothing deleted).
	 */
	deleteMe = (payload: { password?: string }): Promise<void> => {
		return API.delete("me", { data: payload }).then(() => {});
	};
}

const UserService = new UserClass();
export default UserService;
